import { Select } from "antd";
import clsx from 'clsx';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import query from "../../../../../../../server/constants.api";
import useFetch from "../../../../../../../server/useFetch";
import { thresholdsReturnOptions } from "../../ThresholdsConfig/ThresholdsConfig";

const useThresholdsItemModal = ({ selectedTenant, selectedItem, disableFields, modalConfigs, isCreateModalOpen, isEditModalOpen,
    itemName, ...props }) => {
    const { t } = useTranslation();
    const [isSwitchOn, setIsSwitchOn] = useState(false);
    const [selectedIncidentService, setSelectedIncidentService] = useState(null);

    const {
        data: categories,
        isFetching: isFetchingCategories,
        refetch: refetchCategories,
        remove: removeCategories
    } = useFetch(
        '/configurations/thresholds/categories',
        'getCategoriesForThresholds',
        {
            tenant: selectedTenant,
            service: selectedIncidentService
        },
        false,
        null,
        query.middlewareURL);

    useEffect(() => {
        if (selectedIncidentService) {
            refetchCategories();
        }
    }, [selectedIncidentService]);

    /**
     * When create modal is closed, set switch to false.
     */
    useEffect(() => {
        if (!isCreateModalOpen) {
            setIsSwitchOn(false);
            setSelectedIncidentService();
            removeCategories();
        }
    }, [isCreateModalOpen]);

    const hasNoIncidentResults = !modalConfigs?.incidentService?.result?.length > 0;

    /**
     * Determines whether to show the generate incident inputs based on the current state.
     * @returns {boolean} True if the generate incident inputs should be shown, false otherwise.
     */
    const showGenerateIncidentInputs = () => {
        if (isCreateModalOpen) {
            return isSwitchOn;
        } else {
            return !!selectedItem?.generateIncident;
        }
    }

    /**
     * Returns the description for the switch based on the current modal state.
     * @returns {string|null} The description for the switch, or null if no description is available.
     */
    const switchDescription = () => {
        if (isCreateModalOpen && hasNoIncidentResults) {
            return t('message.services_not_found');
        } else if (isEditModalOpen) {
            return t('message.generate_service_not_editable');
        }
        return null;
    }

    const ThresholdsAddModalConfig = [
        {
            title: t('2. Create Threshold'),
            dataIndex: 'title',
            text: (<h3>{t('2. Create Threshold')}</h3>),
            fieldTypeId: 1,
            size: 24,
            isVisible: isCreateModalOpen
        },
        {
            title: `${t('Selected Item')}: ${itemName}`,
            dataIndex: 'selectedItem',
            fieldTypeId: 1,
            text: (<p>{selectedItem?.name}</p>),
            isVisible: selectedItem != null,
            size: 24
        },
        {
            title: t('Label'),
            dataIndex: 'label',
            fieldTypeId: 3,
            required: true,
            child: true,
            disabled: disableFields,
            size: 8,
            showSearch: true
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            fieldTypeId: 3,
            required: true,
            child: true,
            disabled: disableFields,
            size: 8,
        },
        {
            title: t('Open Threshold'),
            dataIndex: 'openThreshold',
            fieldTypeId: 2,
            required: true,
            rule: /^\d+(.\d+)?$/,
            child: true,
            disabled: disableFields,
            size: 8,
        },
        {
            title: t('Urgency'),
            dataIndex: 'urgency',
            fieldTypeId: 3,
            required: true,
            child: true,
            disabled: disableFields,
            size: 8
        },
        {
            title: t('Event Description'),
            dataIndex: 'eventDescription',
            fieldTypeId: 2,
            required: true,
            child: true,
            disabled: disableFields,
            size: 8
        },
        {
            title: t('Close Threshold'),
            dataIndex: 'closeThreshold',
            fieldTypeId: 2,
            rule: /^\d+(.\d+)?$/,
            child: true,
            disabled: disableFields,
            size: 8
        },
        {
            title: t('Is Default'),
            dataIndex: 'isDefault',
            fieldTypeId: 5,
            disabled: disableFields
        },
        {
            title: t('Generate Incident'),
            dataIndex: 'generateIncident',
            fieldTypeId: 11,
            switchFn: (e) => { setIsSwitchOn(e) },
            disabled: disableFields || !isCreateModalOpen || hasNoIncidentResults,
            description: switchDescription(),
        },
        {
            title: t('Incident Service'),
            dataIndex: 'incidentService',
            isVisible: showGenerateIncidentInputs(),
            fieldTypeId: 6,
            required: true,
            customObject: (form) => {
                return (
                    <Select
                        listHeight={200}
                        size={'large'}
                        bordered={false}
                        options={thresholdsReturnOptions('incidentService', modalConfigs, isFetchingCategories, true)}
                        placeholder={< span style={{ color: "lightgrey" }}> {t("Select Value")}</span >}
                        disabled={!isCreateModalOpen}
                        onChange={(e, record) => {
                            setSelectedIncidentService(e);
                        }}
                        className={clsx('select-atomic', props?.className, 'full-width')} />
                )
            }
        },
        {
            title: t('Incident Category'),
            dataIndex: 'incidentCategory',
            isVisible: showGenerateIncidentInputs(),
            fieldTypeId: 6,
            required: true,
            customObject: (form) => {
                return (
                    <Select
                        listHeight={200}
                        size={'large'}
                        bordered={false}
                        disabled={!isCreateModalOpen}
                        options={thresholdsReturnOptions('incidentCategory', categories)}
                        placeholder={< span style={{ color: "lightgrey" }}> {t("Select Value")}</span >}
                        onChange={(e, record) => {
                            // setSelectedIncidentService(e);
                        }}
                        className={clsx('select-atomic', props?.className, 'full-width')} />
                )
            }
        },
    ];

    return ThresholdsAddModalConfig;
}

export default useThresholdsItemModal;